import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { FirestickConfig } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Loading from "../../components/common/Loading";
import FirestickConfigForm from "../../components/ScreenFirestick/FirestickConfigForm";

const Firestick: React.FC = observer(() => {
  const { configStore } = useStores();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFirestickConfig = useCallback(async () => {
    await configStore.fetchFirestickConfig().catch((err) => {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
    });
  }, [enqueueSnackbar, configStore]);

  const handleSubmit = useCallback(
    async (data: FirestickConfig) => {
      setIsSubmitting(true);
      try {
        if (data.id) {
          await configStore.updateFirestickConfig(data);
        } else {
          await configStore.addFirestickConfig(data);
        }
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [enqueueSnackbar, configStore, t]
  );

  useEffect(() => {
    fetchFirestickConfig();
  }, [fetchFirestickConfig]);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.firestickConfig.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.firestickConfig.breadcrumb.firestickConfig")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.firestickConfig.pageHeader.title")}
          buttonEnable={false}
        />
      </Content>
      {configStore.isLoading ? (
        <Loading />
      ) : (
        <FirestickConfigForm
          isSubmitting={isSubmitting}
          onSubmit={(data: any) => handleSubmit(data)}
          firestickConfig={configStore.firestickConfig}
        />
      )}
    </Layout>
  );
});

export default Firestick;
